// Signup page for the free AI-literacy learning sessions.
//
// Ported from the standalone learning app (Vite) into this CRA app when the two
// backends were merged. Behaviour is unchanged; the differences are:
//   - API base comes from the same same-origin logic as services/api.js instead
//     of import.meta.env, and endpoints are under /api/learning/.
//   - Styles are scoped under .learning-signup (see LearningSignup.css).
//
// Rendered without the site Layout: this page is the whole of
// learning.cloverridgetech.com and brings its own hero and footer.

import { useState, useEffect } from 'react';
import './LearningSignup.css';

// Mirrors resolveBaseUrl() in services/api.js: every cloverridgetech.com host
// calls the API same-origin, which keeps CSP, CORS and cookies happy.
const resolveApiBase = () => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'cloverridgetech.com' || host.endsWith('.cloverridgetech.com')) {
      return '/api/learning';
    }
  }
  const base = process.env.REACT_APP_API_BASE_URL || 'https://cloverridgetech.com/api';
  return `${base}/learning`;
};

const API = resolveApiBase();

const TZ = 'America/New_York';

function formatSlot(iso) {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: TZ,
  }) + ' ET';
}

function groupByDay(slots) {
  const map = {};
  for (const s of slots) {
    const key = new Date(s.start_time).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', timeZone: TZ,
    });
    (map[key] = map[key] || []).push(s);
  }
  return map;
}

const SLOTS_PER_PAGE = 4;

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth <= 640);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth <= 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return mobile;
}

export default function LearningSignup() {
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    session_preference: 'one_on_one',
    ai_familiarity: '',
    slot_id: null,
  });
  const [slots, setSlots] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [slotPage, setSlotPage] = useState(0);
  const isMobile = useIsMobile();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    fetch(`${API}/slots/?session_type=${form.session_preference}`)
      .then((r) => r.json())
      .then((data) => setSlots(Array.isArray(data) ? data : data.results || []))
      .catch(() => setSlots([]));
  }, [form.session_preference]);

  async function onSubmit(e) {
    e.preventDefault();
    if (slots.length > 0 && !form.slot_id) {
      setError('Please pick a time slot before submitting.');
      return;
    }
    setStatus('submitting');
    setError('');
    try {
      const res = await fetch(`${API}/signup/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(JSON.stringify(j) || 'Signup failed');
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  }

  if (status === 'success') {
    return (
      <div className="learning-signup">
        <div className="page">
          <div className="card success">
            <h2><span className="clover-mark">☘</span> You're in.</h2>
            <p>
              {form.slot_id
                ? 'Your session is booked. Check your inbox for confirmation.'
                : "Check your inbox — I'll reach out within 48 hours to schedule your session."}
            </p>
          </div>
          <footer>
            <a href="https://cloverridgetech.com" className="footer-link">cloverridgetech.com</a>
            <span>Clover Ridge Technologies</span>
          </footer>
        </div>
      </div>
    );
  }

  const grouped = groupByDay(slots);
  const hasSlots = slots.length > 0;

  return (
    <div className="learning-signup">
      <div className="page">
        <div className="hero">
          <h1><span className="clover-mark">☘</span> Clover Ridge Technologies</h1>
          <p>Free 1-hour technology learning sessions. 1-on-1 or small group. Virtual or in-person. Pick a time that works for you.</p>
        </div>

        <form className="card" onSubmit={onSubmit}>
          {error && <div className="error full">{error}</div>}

          <div className="form-grid">
            <div className="field">
              <label>Full name <span className="req">*</span></label>
              <input required value={form.full_name} onChange={update('full_name')} />
            </div>

            <div className="field">
              <label>Email <span className="req">*</span></label>
              <input required type="email" value={form.email} onChange={update('email')} />
            </div>

            <div className="field">
              <label>Phone</label>
              <input value={form.phone} onChange={update('phone')} placeholder="optional" />
            </div>

            <div className="field">
              <label>Session preference</label>
              <select value={form.session_preference} onChange={(e) => setForm({ ...form, session_preference: e.target.value, slot_id: null })}>
                <option value="one_on_one">1-on-1</option>
                <option value="group">Group</option>
              </select>
            </div>

            <div className="field full">
              <label>Pick a time <span className="req">*</span> {hasSlots ? '' : "(none available — submit anyway and I'll reach out)"}</label>
              {hasSlots && !isMobile && (
                <div className="slots">
                  {Object.entries(grouped).map(([day, daySlots]) => (
                    <div key={day} className="slot-day">
                      <div className="slot-day-label">{day}</div>
                      <div className="slot-row">
                        {daySlots.map((s) => {
                          const selected = form.slot_id === s.id;
                          return (
                            <button
                              type="button"
                              key={s.id}
                              className={'slot-btn' + (selected ? ' selected' : '')}
                              onClick={() => setForm({ ...form, slot_id: selected ? null : s.id })}
                            >
                              {formatSlot(s.start_time)}
                              <span className="slot-mode">{s.mode === 'virtual' ? 'Virtual' : 'In person'}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {hasSlots && isMobile && (
                <>
                  <button type="button" className="slot-picker-btn" onClick={() => { setSlotPage(0); setShowSlotModal(true); }}>
                    {form.slot_id
                      ? `✓ ${formatSlot(slots.find((s) => s.id === form.slot_id)?.start_time)}`
                      : 'Browse available times'}
                  </button>
                  {showSlotModal && (
                    <div className="slot-modal-overlay" onClick={() => setShowSlotModal(false)}>
                      <div className="slot-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="slot-modal-header">
                          <h3>Pick a time</h3>
                          <button type="button" className="slot-modal-close" onClick={() => setShowSlotModal(false)}>✕</button>
                        </div>
                        <div className="slot-modal-body">
                          {slots.slice(slotPage * SLOTS_PER_PAGE, (slotPage + 1) * SLOTS_PER_PAGE).map((s) => {
                            const selected = form.slot_id === s.id;
                            return (
                              <button
                                type="button"
                                key={s.id}
                                className={'slot-btn' + (selected ? ' selected' : '')}
                                onClick={() => { setForm({ ...form, slot_id: selected ? null : s.id }); setShowSlotModal(false); }}
                              >
                                {formatSlot(s.start_time)}
                                <span className="slot-mode">{s.mode === 'virtual' ? 'Virtual' : 'In person'}</span>
                              </button>
                            );
                          })}
                        </div>
                        <div className="slot-modal-nav">
                          <button type="button" disabled={slotPage === 0} onClick={() => setSlotPage(slotPage - 1)}>← Previous</button>
                          <span className="slot-modal-count">{slotPage + 1} / {Math.ceil(slots.length / SLOTS_PER_PAGE)}</span>
                          <button type="button" disabled={(slotPage + 1) * SLOTS_PER_PAGE >= slots.length} onClick={() => setSlotPage(slotPage + 1)}>Next →</button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="field full">
              <label>What topics interest you, and what do you want to get out of this?</label>
              <textarea required value={form.ai_familiarity} onChange={update('ai_familiarity')} />
            </div>
          </div>

          <button disabled={status === 'submitting'}>
            {status === 'submitting' ? 'Sending…' : form.slot_id ? 'Book my session' : 'Reserve my seat'}
          </button>
        </form>
        <footer>
          <a href="https://cloverridgetech.com" className="footer-link">cloverridgetech.com</a>
          <span>Clover Ridge Technologies</span>
        </footer>
      </div>
    </div>
  );
}
